import { MARK_DAMAGE_MULT } from '../../data/abilities.js';
import type { MatchState } from '../State.js';
import type { ProjectileTarget } from '../entities/Projectile.js';
import type { Squad } from '../entities/Squad.js';
import type { PlayerId } from '../ids.js';

// Shared damage routing — used by both CombatSystem (projectile resolution) and
// AbilitySystem (Saboteur mark damage). Lives in its own module so neither
// system imports the other (lint forbids cycles).
//
// Order of effects mirrors the V1 design:
//   1. Mark bonus (CombatSystem path) — handled inline in routeDamage.
//   2. Phase Shield absorb — squad-only; ally bubble eats up to remaining pool.
//   3. Core armor scaling — core-only; halves damage above 40% Core HP.
//
// For Saboteur damage applied via runMarkTarget, the caller passes
// `applyMarkBonus: false` because the mark is set AFTER the damage is dealt
// (so the mark bonus would otherwise self-apply).

// V1.5 Core armor scaling per docs/02_GAME_DESIGN.md §7. While a Core's HP is
// strictly above the threshold fraction of maxHp, incoming damage is multiplied
// by CORE_ARMOR_SCALING_FACTOR_ABOVE; at-or-below the threshold, full damage
// applies.
export const CORE_ARMOR_SCALING_THRESHOLD = 0.4;
export const CORE_ARMOR_SCALING_FACTOR_ABOVE = 0.5;
export const CORE_ARMOR_SCALING_FACTOR_BELOW = 1.0;

export function routeDamage(
  state: MatchState,
  t: ProjectileTarget,
  damage: number,
  attackerId: PlayerId | null,
  opts: { applyMarkBonus?: boolean } = {},
): void {
  const applyMarkBonus = opts.applyMarkBonus ?? true;
  if (t.kind === 'squad') {
    const sq = state.squads[t.id];
    if (sq === undefined) return;
    let dmg = damage;
    if (
      applyMarkBonus &&
      attackerId !== null &&
      sq.markedByPlayerId === attackerId &&
      state.tick < sq.markedUntilTick
    ) {
      dmg *= MARK_DAMAGE_MULT;
    }
    dmg = absorbFromBubble(state, sq.ownerId, sq.pos, dmg);
    if (dmg <= 0) return;
    sq.hp = Math.max(0, sq.hp - dmg);
    // Track who dealt the most recent damage for killfeed attribution.
    // null means the neutral camp was last to hit; a player id means a player.
    sq.lastAttackerPlayerId = attackerId;
    // V1.5+ Slice 2 — taking damage cancels any in-progress channel (Cut Link
    // is the only one in V1.5+ Slice 2). The channel never lands; cooldown was
    // already deducted at startCutLinkChannel time, which is the correct
    // counterplay incentive (defender shoots the Recon → Recon eats both the
    // damage AND the cooldown). Bubble-absorbed damage (dmg <= 0 above)
    // doesn't interrupt — by design, an ally shielding the channel is the
    // intended counter-counterplay.
    sq.channelingAbility = null;
    syncMembersToHp(sq);
    return;
  }
  if (t.kind === 'core') {
    const core = state.cores[t.id];
    if (core === undefined) return;
    const ratio = core.maxHp > 0 ? core.hp / core.maxHp : 0;
    const scale = ratio > CORE_ARMOR_SCALING_THRESHOLD
      ? CORE_ARMOR_SCALING_FACTOR_ABOVE
      : CORE_ARMOR_SCALING_FACTOR_BELOW;
    core.hp = Math.max(0, core.hp - damage * scale);
    return;
  }
  if (t.kind === 'neutral_unit') {
    // V1.5 neutral camp guards. No mark bonus, no Phase Shield, no Core armor —
    // they're a flat hp pool. CampSystem reaps them when hp <= 0. Telemetry:
    // attribute the damage to the camp's tier so CampSystem.maybeClearCamp
    // can name the clearer on the active→cleared transition. Only attribute
    // PLAYER damage (attackerId !== null) so cross-camp neutral fire (e.g.,
    // a friendly-fire scenario in a future map) doesn't leak into the
    // clearer signal.
    const u = state.neutralUnits[t.id];
    if (u === undefined) return;
    const applied = Math.min(u.hp, damage);
    u.hp = Math.max(0, u.hp - damage);
    if (attackerId !== null) {
      const camp = state.neutralCamps[u.campId];
      if (camp !== undefined) {
        const counters = camp.tier === 1
          ? state.stats.campTier1DamageByPlayer
          : state.stats.campTier2DamageByPlayer;
        counters[attackerId] += applied;
      }
    }
    return;
  }
  const b = state.buildings[t.id];
  if (b === undefined) return;
  b.hp = Math.max(0, b.hp - damage);
}

// Subtract from the first matching ally bubble; return the leftover damage that
// still needs to hit the squad. Bubbles do not stack; first match wins so damage
// is predictable.
export function absorbFromBubble(
  state: MatchState,
  ownerId: PlayerId,
  pos: { x: number; y: number },
  damage: number,
): number {
  for (const bubble of Object.values(state.shieldBubbles)) {
    if (bubble.ownerId !== ownerId) continue;
    if (bubble.remaining <= 0) continue;
    if (state.tick >= bubble.expiresAtTick) continue;
    const dx = bubble.pos.x - pos.x;
    const dy = bubble.pos.y - pos.y;
    if (dx * dx + dy * dy > bubble.radius * bubble.radius) continue;
    if (bubble.remaining >= damage) {
      bubble.remaining -= damage;
      return 0;
    }
    const through = damage - bubble.remaining;
    bubble.remaining = 0;
    return through;
  }
  return damage;
}

// Members die in order as the squad's hp crosses per-member thresholds.
// With n members, alive count = ceil(n * hp / maxHp), or 0 if hp is 0.
function syncMembersToHp(squad: Squad): void {
  const n = squad.members.length;
  if (n === 0) return;
  const aliveTarget = squad.hp <= 0 ? 0 : Math.max(1, Math.ceil((n * squad.hp) / squad.maxHp));
  let alive = 0;
  for (const m of squad.members) if (m.alive) alive++;
  if (alive <= aliveTarget) return;
  let toKill = alive - aliveTarget;
  for (let i = squad.members.length - 1; i >= 0 && toKill > 0; i--) {
    const member = squad.members[i];
    if (member !== undefined && member.alive) {
      member.alive = false;
      toKill--;
    }
  }
}
